import {
  Box,
  Button,
  ButtonGroup,
  Divider,
  Flex,
  FormControl,
  FormLabel,
  Tooltip,
  useDisclosure,
} from "@chakra-ui/react";
import { zodResolver } from "@hookform/resolvers/zod";
import { RiArchive2Line, RiMarkdownFill } from "@remixicon/react";
import { useMemo } from "react";
import { FormProvider, useForm } from "react-hook-form";

import { Restricted } from "@/app/components/Restricted";
import { CopyInput } from "@/common/components/CopyInput";
import { FooterNote } from "@/common/components/FooterNote";
import { FormIconPicker } from "@/common/components/Form/FormIconPicker";
import FormInput from "@/common/components/Form/FormInput";
import FormReset from "@/common/components/Form/FormReset";
import { FormRootError } from "@/common/components/Form/FormRootError";
import FormSubmit from "@/common/components/Form/FormSubmit";
import FormSwitch from "@/common/components/Form/FormSwitch";
import FormTextArea from "@/common/components/Form/FormTextArea";
import InfoIconTooltip from "@/common/components/InfoIconTooltip";
import { useFormMutationSubmitHandler } from "@/common/hooks/useApiForm";
import { useSlugParam } from "@/common/hooks/useParam";
import { ArchivingModal } from "@/feature/components/ArchivingModal";
import { LinearIconSyncToggle } from "@/feature/components/LinearIconSyncToggle";
import { SettingsDelete } from "@/feature/components/SettingsDelete";
import { useFeatureData } from "@/feature/data/useFeatureData";
import { useFeatureUpdateMutation } from "@/feature/data/useFeatureUpdateMutation";
import { useFlag } from "@reflag/react-sdk";
import {
  FeatureDetail,
  PatchFeatureArgsSchema,
} from "@reflag/shared/featureAPI";
import { resolveLinearIconAndColor } from "@reflag/shared/icons";
import { z } from "@reflag/shared/utils/zod";

const formSchema = PatchFeatureArgsSchema.omit({
  icon: true,
  iconColor: true,
}).extend({
  icon: z.object({
    icon: z.string().nullish(),
    color: z.string().nullish(),
  }),
  autoSyncLinearIcon: z.boolean().optional(),
});

function defaultValues(feature?: FeatureDetail) {
  return {
    name: feature?.name,
    description: feature?.description ?? undefined,
    icon: {
      icon: feature?.icon,
      color: feature?.iconColor,
    },
    secret: feature?.secret,
    isPermanent: feature?.isPermanent,
    autoSyncLinearIcon: feature?.integrations.linear?.autoSyncIcon,
  };
}

export function SettingsGeneral() {
  const { featureId } = useSlugParam("feature");
  const { data: feature } = useFeatureData(featureId!);
  const { isEnabled: archivingEnabled } = useFlag("archiving");
  const archivingModalDisclosure = useDisclosure();

  const form = useForm<z.input<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    mode: "onChange",
    values: defaultValues(feature),
  });

  const updateMutation = useFeatureUpdateMutation();
  const submitHandler = useFormMutationSubmitHandler(
    form,
    updateMutation,
    (result) => {
      form.reset(defaultValues(result));
    },
    {
      prepareVariables: ({ icon, autoSyncLinearIcon, ...values }) => ({
        ...values,
        id: featureId!,
        icon: icon.icon,
        iconColor: icon.color,
        integrations: {
          linear: {
            autoSyncIcon: autoSyncLinearIcon,
          },
        },
      }),
    },
  );

  const autoSyncLinearIcon = form.watch("autoSyncLinearIcon");
  const secret = form.watch("secret");
  const secretDisabledByOptIn = !!feature?.optInEnabled && !secret;
  const secretDisabledReason =
    "It's not possible to make this flag secret as long as End-user opt-in is enabled. Opt-ins happen directly from the browser/client and thus require the flag to be publicly available.";
  const missingLinearMatch = useMemo(() => {
    const linear = feature?.integrations.linear;
    const linearIcon = linear?.trackingEntity?.icon;
    const resolvedIcon = resolveLinearIconAndColor(linear?.trackingEntity).icon;
    return !!linear && autoSyncLinearIcon && !!linearIcon && !resolvedIcon
      ? linearIcon
      : undefined;
  }, [feature, autoSyncLinearIcon]);

  return (
    <Flex direction="column" gap={4} maxW="mediumForm">
      <Restricted alertStyle="callout" feature={feature} role="contributor">
        <FormProvider {...form}>
          <Flex as="form" direction={"column"} gap={4} onSubmit={submitHandler}>
            <Box as="fieldset">
              <FormLabel htmlFor="feature-name">Name</FormLabel>

              <Flex direction="row" gap={2}>
                <FormIconPicker
                  name="icon"
                  placeholder="Select an icon"
                  rightAction={
                    feature &&
                    ((contentRef) => (
                      <LinearIconSyncToggle
                        feature={feature}
                        missingMatch={missingLinearMatch}
                        onChange={(result) => {
                          if (result) {
                            form.setValue(
                              "icon",
                              {
                                icon: result.icon,
                                color: result.iconColor,
                              },
                              {
                                shouldDirty: true,
                                shouldValidate: true,
                              },
                            );
                          }
                          contentRef?.current?.focus();
                        }}
                      />
                    ))
                  }
                  onChange={() => {
                    form.setValue("autoSyncLinearIcon", false, {
                      shouldDirty: true,
                      shouldValidate: true,
                    });
                  }}
                />

                <FormInput
                  autoComplete="off"
                  id="feature-name"
                  name="name"
                  placeholder="e.g. Huddle calls"
                  size="sm"
                  autoFocus
                  data-1p-ignore
                />
              </Flex>
            </Box>

            <FormTextArea
              description={
                <FooterNote
                  icon={<RiMarkdownFill size={16} />}
                  text="Supports markdown"
                  tooltip={{
                    text: "Common syntax is supported, including *italics*, **bold**, [link](https://example.com) and `code`.",
                    docsURL: "https://www.markdownguide.org/cheat-sheet",
                  }}
                />
              }
              label="Description"
              minH={"20em"}
              name="description"
              placeholder="Add a description..."
            />

            <FormSwitch
              label={
                <>
                  Secret flag{" "}
                  <InfoIconTooltip
                    _content={{ lineHeight: "base", whiteSpace: "normal" }}
                    text={
                      "Secret flags can only be retrieved by a server-side SDK which uses a secret key as opposed to a publishable key. This is useful for flags that should not be exposed to in browsers etc. Secret flags cannot use end-user opt-in."
                    }
                  />
                </>
              }
              isDisabled={secretDisabledByOptIn}
              name="secret"
              switchWrapper={(switchElement) => (
                <Tooltip
                  hasArrow
                  isDisabled={!secretDisabledByOptIn}
                  label={secretDisabledReason}
                  placement="top"
                  shouldWrapChildren
                >
                  {switchElement}
                </Tooltip>
              )}
            />

            {archivingEnabled && (
              <>
                <FormSwitch label="Permanent flag" name="isPermanent" />
              </>
            )}
            <FormControl
              display="flex"
              flexDirection="row"
              isDisabled={form.formState.isSubmitting}
            >
              <FormLabel>Flag ID</FormLabel>
              <CopyInput ml="auto" value={feature?.id ?? ""} w={36} />
            </FormControl>

            <FormRootError />

            <ButtonGroup>
              <FormSubmit showPostSubmit />
              <FormReset />
            </ButtonGroup>
          </Flex>
        </FormProvider>
      </Restricted>

      <Divider />

      <Restricted alertStyle="none" feature={feature} role="publisher">
        {feature && (
          <ButtonGroup>
            <SettingsDelete feature={feature} />
            {archivingEnabled && !feature.isArchived && (
              <>
                <Button
                  leftIcon={<RiArchive2Line size={16} />}
                  variant="outline"
                  onClick={archivingModalDisclosure.onOpen}
                >
                  Archive flag...
                </Button>
                <ArchivingModal
                  featureId={feature.id}
                  modalDisclosure={archivingModalDisclosure}
                />
              </>
            )}
          </ButtonGroup>
        )}
      </Restricted>
    </Flex>
  );
}
